import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import CreateChannelDialog from './CreateChannelDialog';

interface Channel {
  id: string;
  name: string;
  description: string | null;
  member_count: number;
}

function ChannelList() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateChannel, setShowCreateChannel] = useState(false);

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .order('member_count', { ascending: false });

      if (error) throw error;
      setChannels(data || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Channels</h2>
        <button
          onClick={() => setShowCreateChannel(true)}
          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-2">
        {channels.map((channel) => (
          <Link
            key={channel.id}
            to={`/channel/${channel.name}`}
            className="block p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">#{channel.name}</h3>
                {channel.description && (
                  <p className="text-sm text-gray-600 line-clamp-1">
                    {channel.description}
                  </p>
                )}
              </div>
              <div className="flex items-center text-gray-500 text-sm">
                <Users className="h-4 w-4 mr-1" />
                {channel.member_count}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <CreateChannelDialog
        isOpen={showCreateChannel}
        onClose={() => setShowCreateChannel(false)}
        onSuccess={fetchChannels}
      />
    </div>
  );
}

export default ChannelList;